import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  withStyles,
} from '@material-ui/core';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { DatePicker } from '@material-ui/pickers';
import { yupResolver } from '@hookform/resolvers/yup';
import TimePicker from 'components/CustomInputs/TimePicker';
import theme, { colors, fonts } from 'theme';
import { useInventoriedInboundStocks, useStreams } from 'hooks';
import { ValidateIcon } from 'components/Icons';
import { Event, InventoriedInboundStock } from 'types';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import { AppContext } from 'context';
import { notify } from 'components/Notifier';
import { formatInventoriedInboundStocksBeforePost } from 'utils/normalizer';
import { postInventoriedInboundStocks } from 'services/apiOperations';
import { transformId } from 'services/apiPlatformTransformer';
import CustomSwitch from 'components/Buttons/CustomSwitch';
import { ClosureContext } from 'pages/Closures/ClosureClose';
import { createDatePlusTimezoneOffsetFromDateString } from 'utils/date';
import { inventoriedInboundStockUpdateFormSchema } from './InventoriedInboundStockForm.yup';
import { DEFAULT_ITEMS_PER_SELECT } from '../../../tableFilterContext';
import { FormContextActionEnum, setFormContextByAction } from './helper';

const useStyles = makeStyles({
  streamCell: {
    width: '20%',
  },
  weightCell: {
    width: '20%',
  },
  inventaireCheckboxCell: {
    width: '20%',
  },
  separationDateCell: {
    width: '20%',
  },
  separationHourCell: {
    width: '20%',
  },
  headerCell: {
    padding: '11px 22px',
  },
  inboundStocksTable: {
    width: '65%',
    marginBottom: 25,
    height: 320,
  },
  tableContainer: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
  },
  bodyCell: {
    padding: '5px 22px',
    border: `1px solid ${colors.greyLighten20}`,
    maxHeight: 3,
    '&:first-child': {
      borderLeft: 'none',
    },
    '&:last-child': {
      borderRight: 'none',
    },
  },
  updateButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
  actionButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  actionButton: {
    margin: '1%',
  },
  muiTextFieldRoot: {
    '& input': {
      color: theme.palette.text.primary,
      textAlign: 'right',
      fontFamily: fonts.secondary,
    },
  },
  alignRight: {
    textAlign: 'right',
  },
  alignCenter: {
    textAlign: 'center',
  },
  switchInventaireSeparation: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',

    '& > div': {
      width: '65%',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',

      '& > p': {
        marginLeft: 'auto',
      },
    },
  },
});

const StyledTableCell = withStyles({
  root: {
    fontFamily: fonts.secondary,
    fontSize: '16px',
  },
})(TableCell);

type InventoriedInboundStockFormProps = {
  year: string;
  onSubmitProp?: SubmitHandler<InventoriedInboundStockUpdateForm>;
  formName?: string;
  readOnly?: boolean;
  formContext?: ClosureContext;
  setFormContext?: React.Dispatch<React.SetStateAction<ClosureContext | undefined>>;
};

export type InventoriedInboundStockUpdateForm = {
  stocks: InventoriedInboundStock[];
};

export const InventoriedInboundStockForm: React.FC<InventoriedInboundStockFormProps> = ({
  year,
  onSubmitProp,
  formName,
  readOnly,
  formContext,
  setFormContext,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'sortingCenters.inboundStocks' });
  const [editableStocks, setEditableStocks] = useState<InventoriedInboundStock[]>([]);
  const [inventoriedInboundStocks] = useInventoriedInboundStocks(year, '1', DEFAULT_ITEMS_PER_SELECT);
  const [isEditing, setEditing] = useState<boolean>(false);
  const [editButtonsDisabled, setEditButtonsDisabled] = useState<boolean>(false);
  const isDisplayInventaireParSeparation = formContext?.stepInventoriedInboundStock?.isSwitchInventaireOn ?? false;

  const context = useContext(AppContext);
  const sortingCenterId = context.getSortingCenter?.()?.['@id'];

  const [streams] = useStreams(DEFAULT_ITEMS_PER_SELECT, {
    isActiveStream: true,
    sortingCenterId: transformId(sortingCenterId),
    year: year,
  });

  const schema = useMemo(() => inventoriedInboundStockUpdateFormSchema(t), [t]);

  const { control, reset, handleSubmit, setValue } = useForm<InventoriedInboundStockUpdateForm>({
    defaultValues: { stocks: [] },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (!onSubmitProp) return;

    setEditing(true);
  }, [onSubmitProp]);

  useEffect(() => {
    if (!setFormContext) return;
    let atLeastOneSeparationDate = false;

    editableStocks?.forEach((stock, index) => {
      const hasSeparationDate = stock.separationDate ? true : false;
      atLeastOneSeparationDate = atLeastOneSeparationDate ? true : hasSeparationDate;
      setFormContextByAction(
        FormContextActionEnum.UPDATE_INVENTAIRE_PAR_SEPARATION_CHECKBOX,
        { index, checked: hasSeparationDate },
        setFormContext,
      );
    });
    setFormContextByAction(
      FormContextActionEnum.SWITCH_INVENTAIRE_PAR_SEPARATION,
      atLeastOneSeparationDate,
      setFormContext,
    );
  }, [setFormContext, editableStocks]);

  useEffect(() => {
    if (!formContext?.stepInventoriedInboundStock?.inventaireParSeparationCheckboxes) return;

    for (const [index, checked] of Object.entries(
      formContext?.stepInventoriedInboundStock?.inventaireParSeparationCheckboxes,
    )) {
      setValue(`stocks.${parseInt(index)}.inventaireCheckbox`, checked);
    }
  }, [setValue, formContext?.stepInventoriedInboundStock?.inventaireParSeparationCheckboxes]);

  const resetForm = useCallback(() => {
    if (inventoriedInboundStocks && inventoriedInboundStocks.length > 0 && streams) {
      const newEditableStocks = inventoriedInboundStocks.map(stock => {
        const matchingStream = streams.find(stream => stream['@id'] === stock.stream['@id']);
        if (matchingStream) {
          return {
            ...stock,
            stream: matchingStream,
            weight: stock.weight !== undefined ? stock.weight / 1000 : undefined,
            separationDate: stock.separationDate
              ? createDatePlusTimezoneOffsetFromDateString(stock.separationDate).toString()
              : null,
            separationHour: stock.separationDate
              ? createDatePlusTimezoneOffsetFromDateString(stock.separationDate).toString()
              : null,
          };
        } else {
          return {
            ...stock,
            weight: stock.weight !== undefined ? stock.weight / 1000 : undefined,
            separationDate: stock.separationDate
              ? createDatePlusTimezoneOffsetFromDateString(stock.separationDate).toString()
              : null,
            separationHour: stock.separationDate
              ? createDatePlusTimezoneOffsetFromDateString(stock.separationDate).toString()
              : null,
          };
        }
      });
      setEditableStocks(newEditableStocks);
      reset({
        stocks: newEditableStocks.map(stock => ({
          ...stock,
          weight: stock.weight ?? 0,
        })),
      });
      return;
    }
    if (!streams) {
      setEditableStocks([]);
      reset({ stocks: [] });
      return;
    }
    const newEditableStocks = streams.map(stream => ({
      stream,
      year,
      separationDate: null,
      separationHour: null,
      month: '1',
      sortingCenter: '',
    }));
    setEditableStocks(newEditableStocks);
    reset({
      stocks: newEditableStocks.map(stock => ({
        ...stock,
        weight: 0,
      })),
    });
  }, [setEditableStocks, inventoriedInboundStocks, streams, year, reset]);

  useEffect(() => {
    resetForm();
  }, [setEditableStocks, inventoriedInboundStocks, streams, year, reset, resetForm]);

  const handleSwitchInventaire = () => {
    if (undefined === setFormContext) return;
    setFormContextByAction(
      FormContextActionEnum.SWITCH_INVENTAIRE_PAR_SEPARATION,
      !isDisplayInventaireParSeparation,
      setFormContext,
    );
    reset({
      stocks: editableStocks.map(stock => ({
        ...stock,
        separationDate: null,
        separationHour: null,
      })),
    });
  };

  const handleCheckInventaire = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    if (undefined === setFormContext) return;
    const checked = event.target.checked;
    setFormContextByAction(
      FormContextActionEnum.UPDATE_INVENTAIRE_PAR_SEPARATION_CHECKBOX,
      { index, checked },
      setFormContext,
    );

    if (!checked) {
      setValue(`stocks.${index}.separationDate`, null);
      setValue(`stocks.${index}.separationHour`, null);
    }

    setValue(`stocks.${index}.inventaireCheckbox`, checked);
  };

  const onSubmit = useCallback(
    async (e: InventoriedInboundStockUpdateForm) => {
      if (!sortingCenterId) return;

      setEditButtonsDisabled(true);
      const stockCommands = formatInventoriedInboundStocksBeforePost(e, sortingCenterId);

      try {
        await postInventoriedInboundStocks(stockCommands, sortingCenterId);
        notify({
          message: t('inventoryForm.updated'),
          severity: 'success',
        });
        PubSub.publish(Event.REFRESH_INVENTORIED_INBOUND_STOCKS);
        setEditing(false);
      } catch {
        notify({
          message: t('inventoryForm.error'),
          severity: 'error',
        });
      }
      setEditButtonsDisabled(false);
    },
    [sortingCenterId, setEditButtonsDisabled, setEditing, t],
  );

  return (
    <>
      {isEditing && (
        <div className={classes.switchInventaireSeparation}>
          <div>
            <p>{t('inventaireSeparationActive')}</p>
            <CustomSwitch
              disabled={!isEditing}
              checked={isDisplayInventaireParSeparation}
              onChange={handleSwitchInventaire}
            />
          </div>
        </div>
      )}
      <form
        id={formName}
        className={classes.tableContainer}
        onSubmit={onSubmitProp ? handleSubmit(onSubmitProp) : handleSubmit(onSubmit)}
      >
        <TableContainer className={classes.inboundStocksTable}>
          <Table>
            <TableHead>
              <StyledTableCell className={`${classes.headerCell} ${classes.streamCell}`}>{t('stream')}</StyledTableCell>
              <StyledTableCell className={`${classes.headerCell} ${classes.weightCell}`}>{t('weight')}</StyledTableCell>
              {isDisplayInventaireParSeparation && (
                <>
                  <StyledTableCell className={`${classes.headerCell} ${classes.inventaireCheckboxCell}`}>
                    {t('inventaireParSeparation')}
                  </StyledTableCell>
                  <StyledTableCell className={`${classes.headerCell} ${classes.separationDateCell}`}>
                    {t('separationDate')}
                  </StyledTableCell>
                  <StyledTableCell className={`${classes.headerCell} ${classes.separationHourCell}`}>
                    {t('separationHour')}
                  </StyledTableCell>
                </>
              )}
            </TableHead>
            <TableBody>
              {editableStocks.map((editableStock: InventoriedInboundStock, index) => (
                <TableRow key={index}>
                  <StyledTableCell className={`${classes.bodyCell} ${classes.streamCell}`}>
                    {editableStock.stream.name}
                  </StyledTableCell>
                  <StyledTableCell className={`${classes.bodyCell} ${classes.weightCell} ${classes.alignRight}`}>
                    {(!isEditing && editableStock.weight === undefined) ||
                    (editableStock.stream.isActiveStream !== undefined && !editableStock.stream.isActiveStream) ? (
                      '-'
                    ) : (
                      <Controller
                        name={`stocks.${index}.weight`}
                        control={control}
                        render={({ field: { ref, ...inputProps }, fieldState }) => (
                          <WeightInputField
                            className={classes.muiTextFieldRoot}
                            inputRef={ref}
                            disabled={!isEditing}
                            {...inputProps}
                            helperText={fieldState.error?.message}
                            error={fieldState.invalid}
                          />
                        )}
                      />
                    )}
                  </StyledTableCell>
                  {isDisplayInventaireParSeparation && (
                    <>
                      <StyledTableCell className={`${classes.bodyCell} ${classes.weightCell} ${classes.alignCenter}`}>
                        <Controller
                          name={`stocks.${index}.inventaireCheckbox`}
                          control={control}
                          render={({ field: { ref } }) => (
                            <Checkbox
                              disabled={!isEditing}
                              checked={
                                formContext?.stepInventoriedInboundStock?.inventaireParSeparationCheckboxes?.[index]
                              }
                              color={'primary'}
                              onChange={event => handleCheckInventaire(index, event)}
                            />
                          )}
                        />
                      </StyledTableCell>
                      <StyledTableCell
                        className={`${classes.bodyCell} ${classes.separationDateCell} ${classes.alignRight}`}
                      >
                        {(!isEditing && !editableStock.separationDate) ||
                        (editableStock.stream.isActiveStream !== undefined && !editableStock.stream.isActiveStream)
                          ? '-'
                          : formContext?.stepInventoriedInboundStock?.inventaireParSeparationCheckboxes?.[index] && (
                              <Controller
                                name={`stocks.${index}.separationDate`}
                                control={control}
                                render={({ field: { ref, ...fieldProps }, fieldState }) => (
                                  <DatePicker
                                    className={classes.muiTextFieldRoot}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    inputRef={ref}
                                    disableToolbar
                                    clearable
                                    disabled={!isEditing}
                                    clearLabel={t('delete')}
                                    cancelLabel={t('cancel')}
                                    inputVariant="standard"
                                    minDate={new Date(+year - 1, 11, 1)}
                                    maxDate={new Date(+year - 1, 11, 31)}
                                    views={['date']}
                                    format={'dd/MM/yyyy'}
                                    {...fieldProps}
                                  />
                                )}
                              />
                            )}
                      </StyledTableCell>
                      <StyledTableCell
                        className={`${classes.bodyCell} ${classes.separationDateCell} ${classes.alignRight}`}
                      >
                        {(!isEditing && !editableStock.separationDate) ||
                        (editableStock.stream.isActiveStream !== undefined && !editableStock.stream.isActiveStream)
                          ? '-'
                          : formContext?.stepInventoriedInboundStock?.inventaireParSeparationCheckboxes?.[index] && (
                              <Controller
                                name={`stocks.${index}.separationHour`}
                                control={control}
                                render={({ field: { ref, ...fieldProps }, fieldState }) => (
                                  <TimePicker
                                    disabled={!isEditing}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    InputLabelProps={{ required: true }}
                                    inputRef={ref}
                                    {...fieldProps}
                                  />
                                )}
                              />
                            )}
                      </StyledTableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!onSubmitProp && isEditing && !readOnly && (
          <div className={classes.actionButtonContainer}>
            <Button
              onClick={() => {
                resetForm();
                setEditing(false);
              }}
              startIcon={<CancelOutlinedIcon />}
              variant="outlined"
              className={classes.actionButton}
              disabled={editButtonsDisabled}
            >
              {t('cancel')}
            </Button>
            <Button
              endIcon={<ValidateIcon />}
              variant="outlined"
              color={'primary'}
              type="submit"
              className={classes.actionButton}
              disabled={editButtonsDisabled}
            >
              {t('submit')}
            </Button>
          </div>
        )}

        {!isEditing && !readOnly && (
          <div className={classes.updateButtonContainer}>
            <Button
              onClick={() => {
                setEditing(true);
              }}
              variant="outlined"
              color="default"
            >
              {inventoriedInboundStocks && inventoriedInboundStocks.length > 0
                ? t('inventoryForm.update')
                : t('inventoryForm.create')}
            </Button>
          </div>
        )}
      </form>
    </>
  );
};
